import type { z } from "zod/v4";
import type { TenantId } from "@visioni/a3s-schema";
import { Json } from "@visioni/a3s-codecs";
import type { QuerySchema, ResourceConfig, ResourceParams, ResourceScope } from "../resource";
import { expandEndpoint, type EndpointConfig, type MethodWithBody, type ScopedPathParams } from "../endpoint";
import { ClientApiError } from "./query";

/**
 * Mutation variant of {@link ApiConfig}. The tenant fields are flat (rather
 * than nested under `tenant`) because mutations are typically wired up inside
 * components that already have the tenant in scope, and the mutation factory
 * throws at call time — not mount time — when either field is missing.
 */
export interface MutationApiConfig {
  tenantId?: TenantId | undefined;
  apiBaseUrl?: string | undefined;
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

/**
 * Creates a React Query mutation options factory from a POST/PUT/DELETE endpoint.
 *
 * @example
 * const createSnapshot = defineEndpoint(snapshotsResource, {
 *   method: "POST",
 *   bodySchema: CreateSnapshotBodySchema,
 *   responseSchema: CreateSnapshotResponseSchema,
 * });
 * const createSnapshotMutation = mutationFor(createSnapshot);
 * // usage: createSnapshotMutation(apiConfig, pathParams)
 */
export function mutationFor<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TMethod extends MethodWithBody,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    TMethod,
    TResponseSchema,
    TBodySchema,
    TQuerySchema
  >,
): MutationOptionsFactory<TScope, TResourcePathSchema, TQuerySchema, TResponseSchema, TBodySchema>;

export function mutationFor<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TResponseSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    "DELETE",
    TResponseSchema,
    undefined,
    TQuerySchema
  >,
): MutationOptionsFactoryNoBody<TScope, TResourcePathSchema, TQuerySchema, TResponseSchema>;

export function mutationFor<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
  TMethod extends "POST" | "PUT" | "DELETE",
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType | undefined,
  TQuerySchema extends QuerySchema | undefined,
>(
  endpoint: EndpointConfig<
    ResourceConfig<TResource, TResourcePathSchema, TScope>,
    TMethod,
    TResponseSchema,
    TBodySchema,
    TQuerySchema
  >,
) {
  type TResponse = z.output<TResponseSchema>;
  type TQuery = TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined;
  type TExtraPath = z.output<TResourcePathSchema>;
  type TBody = TBodySchema extends z.ZodType ? z.input<TBodySchema> : undefined;

  // Combined path: scoped base params (minus tenantId) + extra params from template
  type TPathInput = Omit<ScopedPathParams<TScope>, "tenantId"> & TExtraPath;
  type TFullPath = ScopedPathParams<TScope> & TExtraPath;

  const { responseSchema, method } = endpoint;
  const bodySchema = "bodySchema" in endpoint ? endpoint.bodySchema : undefined;

  return (apiConfig: MutationApiConfig, pathParams: TPathInput, queryParams?: TQuery) => {
    const { tenantId, apiBaseUrl, authenticatedFetch } = apiConfig;
    const fullPath = { tenantId, ...pathParams } as TFullPath;

    const mutationFn = async (params?: TBody | TQuery): Promise<TResponse> => {
      if (!tenantId || !apiBaseUrl) {
        throw new Error("tenantId and apiBaseUrl are required for mutations");
      }

      let query: TQuery | undefined;
      let body: TBody | undefined;

      // For POST/PUT: params sent as request body
      // For DELETE: params sent as query string
      // DELETE requests with bodies are largely unspecified and much of the industry avoids them. They can work
      // well in limited use, but tend to get complex once more infrastucture is added (proxies, etc). Try to use
      // canonical resource urls or query params.
      if (method === "DELETE") {
        const dynamicQueryParams = params as TQuery | undefined;
        if (queryParams || dynamicQueryParams) {
          query = { ...(queryParams as object), ...(dynamicQueryParams as object) } as TQuery;
        }
      } else {
        body = params as TBody;
        query = queryParams;
      }

      const url = `${apiBaseUrl}${expandEndpoint(endpoint, { path: fullPath, query })}`;

      const init: RequestInit = {
        method: method as string,
      };

      if (body !== undefined && bodySchema) {
        init.headers = { "Content-Type": "application/json" };
        init.body = Json(bodySchema).encode(body);
      }

      const response = await authenticatedFetch(url, init);

      if (!response.ok) {
        let errorMessage = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            // Try JSON first, fall back to plain text
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) {
                errorMessage = json.message;
              }
            } catch {
              errorMessage = text;
            }
          }
        } catch {
          // failed to read response body, use default message
        }
        throw new ClientApiError(response.status, errorMessage);
      }

      // A `204` (or any empty body) has nothing to `json()`: it parses
      // `undefined`, so an endpoint declaring `z.undefined()` resolves and one
      // that needs a body still fails its schema.
      const text = response.status === 204 ? "" : await response.text();
      const data: unknown = text === "" ? undefined : JSON.parse(text);
      return responseSchema.parse(data);
    };

    return { mutationFn };
  };
}

// Helper types for the overloads
type MutationOptionsFactory<
  TScope extends ResourceScope,
  TResourcePathSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
  TResponseSchema extends z.ZodType,
  TBodySchema extends z.ZodType,
> = (
  apiConfig: MutationApiConfig,
  pathParams: Omit<ScopedPathParams<TScope>, "tenantId"> & z.output<TResourcePathSchema>,
  queryParams?: TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined,
) => {
  mutationFn: (body: z.input<TBodySchema>) => Promise<z.output<TResponseSchema>>;
};

type MutationOptionsFactoryNoBody<
  TScope extends ResourceScope,
  TResourcePathSchema extends z.ZodType,
  TQuerySchema extends QuerySchema | undefined,
  TResponseSchema extends z.ZodType,
> = (
  apiConfig: MutationApiConfig,
  pathParams: Omit<ScopedPathParams<TScope>, "tenantId"> & z.output<TResourcePathSchema>,
  queryParams?: TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined,
) => {
  mutationFn: (
    params?: TQuerySchema extends QuerySchema ? z.output<TQuerySchema> : undefined,
  ) => Promise<z.output<TResponseSchema>>;
};
